import { Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

import { formatDateTime, useAppNotification } from '@feature/utils';

import { useUserDocumentAdminDelete, useUserDocumentAdminDownload } from '../model/UserDocumentQuery';
import { UserDocumentSlot } from '../model/UserDocumentTypes';
import { UserDocumentAdminUploadModal } from './UserDocumentAdminUploadModal';
import { UserDocumentSlotActions } from './UserDocumentSlotActions';

interface UserDocumentAdminSlotRowProps {
  userId: number;
  slot: UserDocumentSlot;
}

export function UserDocumentAdminSlotRow({ userId, slot }: UserDocumentAdminSlotRowProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const [uploadOpened, { open: openUpload, close: closeUpload }] = useDisclosure(false);
  const download = useUserDocumentAdminDownload();
  const deleteDocument = useUserDocumentAdminDelete();

  const uploadedDocument = slot.document;

  const onDownload = () =>
    download.mutate(
      { userId, kind: slot.kind, fallbackFilename: uploadedDocument?.file.title ?? slot.kind },
      { onError: notification.error },
    );

  const onDelete = () =>
    deleteDocument
      .mutateAsync(
        { userId, kind: slot.kind },
        {
          onError: notification.error,
          onSuccess: notification.success,
        },
      )
      // Already reported through onError; the confirm dialog only awaits the settle to close.
      .catch(() => undefined);

  return (
    <Group
      role="group"
      aria-label={t(`userDocuments.kindEnum.${slot.kind}`)}
      gap="xs"
      align="center"
      justify="space-between"
      wrap="nowrap"
    >
      <Stack
        gap={2}
        miw={0}
      >
        <Text
          size="sm"
          c={uploadedDocument == null ? 'dimmed' : undefined}
          fs={uploadedDocument == null ? 'italic' : undefined}
        >
          {t(`userDocuments.kindEnum.${slot.kind}`)}: {uploadedDocument?.file.title ?? t('userDocuments.missing')}
        </Text>
        {uploadedDocument != null && (
          <Text
            size="xs"
            c="dimmed"
          >
            {t('userDocuments.uploadedAt')}: {formatDateTime(uploadedDocument.uploadedAt)}
          </Text>
        )}
      </Stack>
      <UserDocumentSlotActions
        hasDocument={uploadedDocument != null}
        onDownload={onDownload}
        isDownloading={download.isPending}
        onUpload={openUpload}
        onDelete={onDelete}
        isDeleting={deleteDocument.isPending}
      />
      {uploadOpened && (
        <UserDocumentAdminUploadModal
          userId={userId}
          kind={slot.kind}
          onClose={closeUpload}
        />
      )}
    </Group>
  );
}
